import { createContext, useEffect, useState, useCallback, ReactNode } from 'react';
import supabase from '../supabase';
import { useSession } from '@/hooks/useSessionHook';
import type {
  EntitlementsContextValue,
  Entitlement,
  Subscription,
} from '@/types/entitlements';

const defaultContext: EntitlementsContextValue = {
  tier: 'free',
  credits: 0,
  flags: {
    canViewImages: false,
    canViewVideos: false,
  },
  subscription: null,
  isLoading: true,
  error: null,
  refresh: async () => {},
};

// eslint-disable-next-line react-refresh/only-export-components
export const EntitlementsContext = createContext<EntitlementsContextValue>(defaultContext);

interface EntitlementsProviderProps {
  children: ReactNode;
}

export function EntitlementsProvider({ children }: EntitlementsProviderProps) {
  const { session } = useSession();
  const userId = session?.user?.id;

  const [entitlement, setEntitlement] = useState<Entitlement | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchEntitlements = useCallback(async () => {
    if (!userId) {
      setEntitlement(null);
      setSubscription(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch entitlements and subscription in parallel
      const [entitlementResult, subscriptionResult] = await Promise.all([
        supabase
          .from('entitlements')
          .select('*')
          .eq('user_id', userId)
          .maybeSingle(),
        supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', userId)
          .maybeSingle(),
      ]);

      if (entitlementResult.error) {
        throw new Error(`Failed to fetch entitlements: ${entitlementResult.error.message}`);
      }

      if (subscriptionResult.error) {
        throw new Error(`Failed to fetch subscription: ${subscriptionResult.error.message}`);
      }

      setEntitlement(entitlementResult.data);
      setSubscription(subscriptionResult.data);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load entitlements');
      console.error('Entitlements fetch error:', error);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  // Initial fetch and realtime subscriptions
  useEffect(() => {
    if (!userId) {
      setEntitlement(null);
      setSubscription(null);
      setIsLoading(false);
      return;
    }

    fetchEntitlements();

    // Subscribe to entitlements changes
    const entitlementsChannel = supabase
      .channel(`entitlements-${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'entitlements',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          if (payload.eventType === 'DELETE') {
            setEntitlement(null);
          } else {
            setEntitlement(payload.new as Entitlement);
          }
        }
      )
      .subscribe();

    // Subscribe to subscription changes
    const subscriptionsChannel = supabase
      .channel(`subscriptions-${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          if (payload.eventType === 'DELETE') {
            setSubscription(null);
          } else {
            setSubscription(payload.new as Subscription);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(entitlementsChannel);
      supabase.removeChannel(subscriptionsChannel);
    };
  }, [userId, fetchEntitlements]);

  // Derive context value from state
  const value: EntitlementsContextValue = {
    tier: entitlement?.tier ?? 'free',
    credits: entitlement?.credits_remaining ?? 0,
    flags: {
      canViewImages: entitlement?.can_view_images ?? false,
      canViewVideos: entitlement?.can_view_videos ?? false,
    },
    subscription,
    isLoading,
    error,
    refresh: fetchEntitlements,
  };

  return (
    <EntitlementsContext.Provider value={value}>
      {children}
    </EntitlementsContext.Provider>
  );
}
